import {Inject, Injector, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {ValidationErrorResponse} from '../generated-client/generated-api-client';
import {BehaviorSubject} from 'rxjs';
import {FormGroup} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {isFormModifiedDataService} from './data-services/navigation-data-services';

type StatusErrorMessage = {
  [key: number]: string;
};

const DEFAULT_ERROR_MESSAGES: StatusErrorMessage = {
  400: 'Sikertelen mentés',
  500: 'Váratlan hiba történt',
  404: 'A keresett elem nem található',
  304: 'A művelet nem hajtódott végre',
}

@Injectable()
export class ErrorProcessorService {
  formGroup?: FormGroup;
  formErrors = new BehaviorSubject<Record<string, string[]> | undefined>(undefined);

  constructor(
    @Inject(Injector) private injector: Injector,
    private router: Router,
    private isFormModifiedDataService: isFormModifiedDataService
  ) {
    this.formErrors.subscribe(errors => {
      if (this.formGroup && errors) {
        for (const key in errors) {
          if (this.formGroup.controls.hasOwnProperty(key)) {
            const control = this.formGroup.controls[key];
            if (control.errors) {
              control.errors!['serverError'] = errors[key];
              control.markAsTouched();
            } else {
              control.setErrors({ serverError: errors[key] });
              control.markAsTouched();
            }
          }
        }
      }
    });
  }

  private get toastrService(): ToastrService {
    return this.injector.get(ToastrService);
  }

  public processError(error: any, showToastr: boolean = false, doRedirect: boolean = false, statusErrorMessages?: StatusErrorMessage, showModelErrors: boolean = true) {
    const statusCode = error.statusCode ?? error.status;
    if (statusCode === 404) {
      this.process404NotFound(error, statusErrorMessages);
    } else if (statusCode === 500) {
      this.process500BadRequest(error, showToastr, doRedirect, statusErrorMessages);
    } else if (statusCode === 400) {
      this.process400BadRequest(error, showToastr, statusErrorMessages, showModelErrors);
    } else if (statusCode === 304) {
      this.process304NotModified(error, showToastr, doRedirect, statusErrorMessages);
    } else {
      this.processDefaultError(error, showToastr, doRedirect, statusErrorMessages);
    }
  }

  private processDefaultError(error: any, showToastr: boolean, doRedirect: boolean = false, statusErrorMessages?: StatusErrorMessage) {
    this.isFormModifiedDataService.isFormModified = false;
    if (showToastr) {
      this.toastrService.error(this.getErrorMessage(500, statusErrorMessages));
    }
    if (doRedirect) {
      this.router.navigate(['error']);
    }
  }

  private process404NotFound(error: any, statusErrorMessages?: StatusErrorMessage) {
    this.isFormModifiedDataService.isFormModified = false;
    this.router.navigate(['error/404']);
  }

  private process500BadRequest(error: any, showToastr: boolean = false, doRedirect: boolean = false, statusErrorMessages?: StatusErrorMessage) {
    this.isFormModifiedDataService.isFormModified = false;
    if (showToastr) {
      this.toastrService.error(this.getErrorMessage(500, statusErrorMessages));
    }
    if (doRedirect) {
      this.router.navigate(['error/500']);
    }
  }

  private process400BadRequest(error: any, showToastr: boolean = false, statusErrorMessages?: StatusErrorMessage, showModelErrors: boolean = true) {
    const errors = (error as ValidationErrorResponse).errors as Record<string, string[]>;

    let errorsShown = false;
    if (errors) {
      const newErrors: Record<string, string[]> = {};
      Object.keys(errors).forEach(key => {
        newErrors[key.charAt(0).toLowerCase() + key.slice(1)] = errors[key];
      });

      this.formErrors.next(newErrors);

      if (showModelErrors && errors['']) {
        for (const err in errors['']) {
          this.toastrService.error(errors[''][err]);
        }
        errorsShown = true;
      }
    }

    if (showToastr && !errorsShown) {
      this.toastrService.error(this.getErrorMessage(400, statusErrorMessages));
    }
  }

  private process304NotModified(error: any, showToastr: boolean, doRedirect: boolean = false, statusErrorMessages?: StatusErrorMessage) {
    this.isFormModifiedDataService.isFormModified = false;
    if (showToastr) {
      this.toastrService.info(this.getErrorMessage(304, statusErrorMessages));
    }
    if (doRedirect) {
      this.router.navigate(['error']);
    }
  }

  private getErrorMessage(statusCode: number, statusErrorMessages?: StatusErrorMessage) {
    return statusErrorMessages?.[statusCode] ?? DEFAULT_ERROR_MESSAGES[statusCode];
  }
}
